import React, { useEffect, useRef, useState } from 'react';
import { arsenalData } from '../data/mock';
import { TrendingUp, Network, Target, Eye } from 'lucide-react';

const iconMap = {
  TrendingUp,
  Network,
  Target,
  Eye
};

// Post-it colors for variety
const postitColors = [
  { bg: '#FFF9E6', shadow: '#D4C47A' },  // Yellow
  { bg: '#E6F3FF', shadow: '#7AB3D4' },  // Blue
  { bg: '#FFE6E6', shadow: '#D47A7A' },  // Pink
  { bg: '#E6FFE6', shadow: '#7AD47A' },  // Green
];

const ArsenalSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const rotations = ['-2deg', '1.5deg', '-1deg', '2deg'];
  const radiuses = [
    '255px 15px 225px 15px / 15px 225px 15px 255px',
    '15px 225px 15px 255px / 255px 15px 225px 15px',
    '225px 15px 255px 15px / 15px 255px 15px 225px',
    '15px 255px 15px 225px / 225px 15px 255px 15px'
  ];

  return (
    <section
      id="arsenal"
      ref={sectionRef}
      className="min-h-screen px-6 py-20 md:pl-24"
    >
      <div className="max-w-6xl mx-auto">
        
        {/* Section Header */}
        <div 
          className={`text-center mb-16 transition-all duration-700
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
          `}
        >
          <h2 className="font-marker text-3xl md:text-4xl lg:text-5xl text-[#2D2D2D] mb-4">
            {arsenalData.title}
          </h2>
          <p className="font-architect text-lg md:text-xl text-[#2D2D2D]/70 max-w-3xl mx-auto">
            {arsenalData.intro}
          </p>
        </div>

        {/* Arsenal Cards Grid - Post-it Style */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
          {arsenalData.cards.map((card, index) => {
            const IconComponent = iconMap[card.icon];
            const color = postitColors[index];
            
            return (
              <div
                key={index}
                className={`p-6 md:p-8 border-[3px] border-[#2D2D2D]
                  transition-all duration-500 cursor-default
                  hover:scale-[1.02] hover:rotate-0
                  ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}
                `}
                style={{ 
                  backgroundColor: color.bg,
                  borderRadius: radiuses[index],
                  boxShadow: `5px 5px 0px ${color.shadow}`,
                  transform: `rotate(${rotations[index]})`,
                  transitionDelay: `${index * 0.15}s`
                }}
              >
                {/* Card Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div 
                    className="p-3 bg-[#1F4E79] border-2 border-[#2D2D2D]"
                    style={{ borderRadius: '255px 15px 225px 15px / 15px 225px 15px 255px' }}
                  >
                    <IconComponent className="w-6 h-6 text-white icon-sketch" />
                  </div>
                  <div>
                    <h3 className="font-patrick text-xl md:text-2xl text-[#2D2D2D]">
                      {card.title}
                    </h3>
                    <span className="font-architect text-sm text-[#F08A24]">
                      {card.subtitle}
                    </span>
                  </div>
                </div>

                {/* Approach */}
                <div className="mb-4">
                  <span className="font-patrick text-sm text-[#1F4E79] uppercase tracking-wide">
                    L&apos;Approche
                  </span>
                  <p className="font-architect text-base text-[#2D2D2D]/85 leading-relaxed mt-1">
                    {card.approach}
                  </p>
                </div>

                {/* Result */}
                <div 
                  className="bg-white/60 p-4 border-2 border-[#2D2D2D]/30"
                  style={{ borderRadius: '15px 225px 15px 255px / 255px 15px 225px 15px' }}
                >
                  <span className="font-patrick text-sm text-[#F08A24] uppercase tracking-wide">
                    Le Résultat
                  </span>
                  <p className="font-architect text-base text-[#2D2D2D]/80 leading-relaxed mt-1">
                    {card.result}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default ArsenalSection;
